import { supabase } from '../lib/supabase';
import { aiMatchingService } from './aiMatchingService';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

// Helper function to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  }
  return camelObj;
}

// Helper function to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
  }
  return snakeObj;
}

export const realTimeMonitoringService = {
  dealMonitorChannel: null,
  tripMonitorChannel: null,
  
  /**
   * Start monitoring deals and trips for automatic rematch
   */
  startMonitoring() {
    this.monitorDeals();
    this.monitorTrips();
  },
  
  /**
   * Stop all monitoring
   */
  stopMonitoring() {
    if (this.dealMonitorChannel) {
      supabase?.removeChannel(this.dealMonitorChannel);
      this.dealMonitorChannel = null;
    }
    if (this.tripMonitorChannel) {
      supabase?.removeChannel(this.tripMonitorChannel);
      this.tripMonitorChannel = null;
    }
  },
  
  /**
   * Monitor deal status changes
   */
  monitorDeals() {
    this.dealMonitorChannel = supabase
      ?.channel('deals-monitor')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deals'
        },
        async (payload) => {
          await this.handleDealChange(payload);
        }
      )
      ?.subscribe();
  },
  
  /**
   * Monitor trip status changes
   */
  monitorTrips() {
    this.tripMonitorChannel = supabase
      ?.channel('trips-monitor')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'traveler_trips'
        },
        async (payload) => {
          await this.handleTripChange(payload);
        }
      )
      ?.subscribe();
  },
  
  /**
   * Handle deal status changes
   */
  async handleDealChange(payload) {
    const deal = toCamelCase(payload?.new);
    const oldDeal = toCamelCase(payload?.old);
    
    // Check if traveler cancelled
    if (deal?.status === 'cancelled' && oldDeal?.status !== 'cancelled') {
      console.log(`🚨 Deal ${deal?.id} cancelled - initiating emergency rematch`);
      await this.emergencyRematch(deal, 'traveler_cancelled');
    }
    
    // Check for delays
    if (deal?.status === 'in_transit' && this.isDelayed(deal)) {
      console.log(`⚠️ Deal ${deal?.id} delayed - analyzing impact`);
      await this.handleDelay(deal);
    }
  },
  
  /**
   * Handle trip status changes
   */
  async handleTripChange(payload) {
    const trip = toCamelCase(payload?.new);
    const oldTrip = toCamelCase(payload?.old);
    
    // Check if trip was cancelled or dates changed significantly
    if (trip?.status === 'cancelled' || 
        (trip?.departureDate !== oldTrip?.departureDate)) {
      
      // Find affected deals
      const { data: affectedDeals } = await supabase?.from('deals')?.select('*')?.eq('traveler_id', trip?.travelerId)?.in('status', ['ai_matched', 'accepted', 'paid']);
      
      if (affectedDeals?.length > 0) {
        console.log(`🚨 Trip ${trip?.id} changed - ${affectedDeals?.length} deals affected`);
        
        for (const deal of affectedDeals) {
          await this.emergencyRematch(toCamelCase(deal), 'trip_changed');
        }
      }
    }
  },
  
  /**
   * Emergency rematch when traveler cancels or becomes unavailable
   */
  async emergencyRematch(deal, triggerReason) {
    const startTime = Date.now();
    
    try {
      console.log(`🔄 Starting emergency rematch for deal ${deal?.id}`);
      
      // Get original request
      const { data: request } = await supabase?.from('purchase_requests')?.select('*')?.eq('id', deal?.requestId)?.single();
      
      if (!request) {
        console.error('Request not found for rematch');
        return;
      }
      
      // Attempt AI rematch with SPEED mode (urgent)
      const rematchResult = await aiMatchingService?.matchRequestWithTraveler(
        request?.id,
        'SPEED'
      );
      
      if (rematchResult?.matched) {
        // Cancel old deal
        await supabase?.from('deals')?.update({ 
            status: 'cancelled',
            updated_at: new Date()?.toISOString()
          })?.eq('id', deal?.id);
        
        // Log emergency rematch event
        const resolutionTime = Math.floor((Date.now() - startTime) / 1000 / 60);
        
        await supabase?.from('emergency_rematch_events')?.insert({
          original_deal_id: deal?.id,
          new_deal_id: rematchResult?.deal?.id,
          trigger_reason: triggerReason,
          resolution_time_minutes: resolutionTime,
          compensation_amount: 0,
          ai_solution: {
            matchScore: rematchResult?.matchScore,
            reasoning: rematchResult?.reasoning,
            executionTime: rematchResult?.executionTime
          }
        });
        
        // Notify buyer
        await this.notifyBuyer(deal?.buyerId, {
          type: 'EMERGENCY_REMATCH',
          dealId: deal?.id,
          newDealId: rematchResult?.deal?.id,
          message: 'Your delivery has been automatically rematched with a new traveler due to unforeseen circumstances.',
          oldTravelerId: deal?.travelerId,
          newTravelerId: rematchResult?.deal?.travelerId
        });
        
        // Notify new traveler
        await this.notifyTraveler(rematchResult?.deal?.travelerId, {
          type: 'NEW_AI_MATCH',
          dealId: rematchResult?.deal?.id,
          message: 'You have been matched with a new delivery request.',
          matchScore: rematchResult?.matchScore
        });
        
        console.log(`✅ Emergency rematch successful in ${resolutionTime} minutes`);
      } else {
        // No alternative found - notify buyer and offer refund
        console.log(`❌ No alternative traveler found - processing refund`);
        
        await supabase?.from('emergency_rematch_events')?.insert({
          original_deal_id: deal?.id,
          new_deal_id: null,
          trigger_reason: triggerReason,
          resolution_time_minutes: null,
          compensation_amount: 0,
          ai_solution: {
            alternatives: rematchResult?.alternatives || []
          }
        });
        
        await this.notifyBuyer(deal?.buyerId, {
          type: 'REMATCH_FAILED',
          dealId: deal?.id,
          message: 'We could not find an alternative traveler. Your request will be reopened for manual matching.',
          alternatives: rematchResult?.alternatives || []
        });
        
        // Reopen request for manual matching
        await supabase?.from('purchase_requests')?.update({ status: 'active' })?.eq('id', deal?.requestId);
      }
    } catch (error) {
      console.error('Emergency rematch error:', error);
    }
  },
  
  /**
   * Handle delivery delays
   */
  async handleDelay(deal) {
    try {
      const prompt = `Analyze this delivery delay and recommend action:

Deal ID: ${deal?.id}
Status: ${deal?.status}
Departure Date: ${deal?.departureDate}
Arrival Date: ${deal?.arrivalDate}
Current Date: ${new Date()?.toISOString()}

Recommend:
1. Can traveler still complete delivery?
2. Should we rematch?
3. Compensation amount (if any)
4. Alternative actions

Return JSON with recommendation.`;
      
      const aiResponse = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'user', content: prompt }],
        {
          response_format: {
            type: 'json_schema',
            json_schema: {
              name: 'DelayAnalysis',
              schema: {
                type: 'object',
                properties: {
                  recommendation: { type: 'string' },
                  canComplete: { type: 'boolean' },
                  shouldRematch: { type: 'boolean' },
                  compensationAmount: { type: 'number' },
                  reasoning: { type: 'string' }
                },
                required: ['recommendation', 'canComplete', 'shouldRematch', 'compensationAmount', 'reasoning'],
                additionalProperties: false
              },
              strict: true
            }
          },
          max_completion_tokens: 500
        }
      );
      
      const analysis = JSON.parse(aiResponse?.choices?.[0]?.message?.content || '{}');
      
      if (analysis?.shouldRematch) {
        await this.emergencyRematch(deal, 'delay_detected');
      } else {
        // Just notify about delay
        await this.notifyBuyer(deal?.buyerId, {
          type: 'DELAY_NOTIFICATION',
          dealId: deal?.id,
          message: `Your delivery may be delayed. ${analysis?.reasoning}`,
          compensationAmount: analysis?.compensationAmount || 0
        });
      }
    } catch (error) {
      console.error('Handle delay error:', error);
    }
  },
  
  /**
   * Check if deal is delayed
   */
  isDelayed(deal) {
    if (!deal?.arrivalDate) return false;
    
    const arrivalDate = new Date(deal?.arrivalDate);
    const now = new Date();
    const daysDiff = Math.floor((now - arrivalDate) / (1000 * 60 * 60 * 24));
    
    return daysDiff > 2; // Delayed if more than 2 days past arrival
  },
  
  /**
   * Notify buyer
   */
  async notifyBuyer(buyerId, notification) {
    try {
      await supabase?.from('notifications')?.insert({
        user_id: buyerId,
        deal_id: notification?.dealId,
        type: notification?.type,
        title: this.getNotificationTitle(notification?.type),
        message: notification?.message,
        metadata: notification
      });
    } catch (error) {
      console.error('Notify buyer error:', error);
    }
  },
  
  /**
   * Notify traveler
   */
  async notifyTraveler(travelerId, notification) {
    try {
      await supabase?.from('notifications')?.insert({
        user_id: travelerId,
        deal_id: notification?.dealId,
        type: notification?.type,
        title: this.getNotificationTitle(notification?.type),
        message: notification?.message,
        metadata: notification
      });
    } catch (error) {
      console.error('Notify traveler error:', error);
    }
  },
  
  /**
   * Get notification title based on type
   */
  getNotificationTitle(type) {
    const titles = {
      EMERGENCY_REMATCH: '🔄 Delivery Rematched',
      NEW_AI_MATCH: '✨ New AI Match',
      REMATCH_FAILED: '⚠️ Rematch Failed',
      DELAY_NOTIFICATION: '⏰ Delivery Delay'
    };
    return titles?.[type] || 'Notification';
  }
};